import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import type { Client } from '../../../services/clientService';
import { currencyService } from '../../../services/currencyService';
import {
  adminPaymentLinkService,
  getInvoicePdfUrl,
  type AdminPaymentLink,
} from '../../../services/adminPaymentLinkService';
import { getAllServices } from '../../../services/servicesStore';
import {
  adminSubscriptionService,
  createEmptySubscription,
  type AdminSubscription,
  type SubscriptionBillingStatus,
  type SubscriptionInterval,
  type SubscriptionPaymentMode,
  type SubscriptionServiceStatus,
  type SubscriptionStatus,
} from '../../../services/adminSubscriptionService';
import type { Service } from '../../../services/servicesStore';
import SubscriptionDetailsModal from './SubscriptionDetailsModal';

interface PaymentSubscriptionsPanelProps {
  clients: Client[];
  services: Service[];
  paymentLinks: AdminPaymentLink[];
  displayCountry: string;
}

type SubscriptionFormState = {
  selectedClientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  selectedServiceId: string;
  serviceName: string;
  amount: string;
  currencyCode: string;
  subscriptionNumber: string;
  interval: SubscriptionInterval;
  status: SubscriptionStatus;
  billingStatus: SubscriptionBillingStatus;
  serviceStatus: SubscriptionServiceStatus;
  startDate: string;
  nextBillingDate: string;
  lastBillingDate: string;
  endDate: string;
  linkedPaymentLinkKey: string;
  linkedOrderNumber: string;
  notes: string;
  internalNotes: string;
  paymentMode: SubscriptionPaymentMode;
  autoCreatePaymentLink: boolean;
  sendPaymentLinkEmail: boolean;
  paymentLinkUpsellItems: SubscriptionPaymentLineItemForm[];
};

type SubscriptionPaymentLineItemForm = {
  id: string;
  label: string;
  quantity: string;
  unitAmount: string;
};

const createSubscriptionPaymentLineItem = (): SubscriptionPaymentLineItemForm => ({
  id: `sub_upsell_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  quantity: '1',
  unitAmount: '',
});

const INTERVAL_OPTIONS: Array<{ value: SubscriptionInterval; label: string }> = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
];

const PAYMENT_MODE_OPTIONS: Array<{ value: SubscriptionPaymentMode; label: string }> = [
  { value: 'recurring', label: 'Récurrent (abonnement)' },
  { value: 'one_time', label: 'Paiement unique' },
];

const STATUS_OPTIONS: Array<{ value: SubscriptionStatus; label: string }> = [
  { value: 'active', label: 'Actif' },
  { value: 'trial', label: 'Essai' },
  { value: 'paused', label: 'En pause' },
  { value: 'past_due', label: 'En retard' },
  { value: 'canceled', label: 'Annule' },
  { value: 'expired', label: 'Expire' },
];

const BILLING_STATUS_OPTIONS: Array<{
  value: SubscriptionBillingStatus;
  label: string;
}> = [
  { value: 'scheduled', label: 'Planifie' },
  { value: 'paid', label: 'Paye' },
  { value: 'overdue', label: 'En retard' },
  { value: 'unpaid', label: 'Impayee' },
];

const SERVICE_STATUS_OPTIONS: Array<{
  value: SubscriptionServiceStatus;
  label: string;
}> = [
  { value: 'pending_setup', label: 'Setup en attente' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active_delivery', label: 'Livraison active' },
  { value: 'delivered', label: 'Livre' },
  { value: 'paused', label: 'En pause' },
  { value: 'closed', label: 'Cloture' },
];

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Essai',
  active: 'Actif',
  paused: 'En pause',
  past_due: 'En retard',
  canceled: 'Annule',
  expired: 'Expire',
};

const STATUS_BADGES: Record<SubscriptionStatus, string> = {
  trial: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non defini';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Non defini';
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const addMonths = (input: string, months: number) => {
  const baseDate = new Date(`${input}T12:00:00`);
  if (Number.isNaN(baseDate.getTime())) {
    return '';
  }

  baseDate.setMonth(baseDate.getMonth() + months);
  return baseDate.toISOString().slice(0, 10);
};

const buildNextBillingDate = (startDate: string, interval: SubscriptionInterval) => {
  if (!startDate) {
    return '';
  }

  if (interval === 'quarterly') {
    return addMonths(startDate, 3);
  }

  if (interval === 'yearly') {
    return addMonths(startDate, 12);
  }

  return addMonths(startDate, 1);
};

const buildInitialState = (currencyCode = 'EUR'): SubscriptionFormState => {
  const base = createEmptySubscription();
  const startDate = new Date().toISOString().slice(0, 10);

  return {
    selectedClientId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientCompany: '',
    selectedServiceId: '',
    serviceName: '',
    amount: '',
    currencyCode,
    subscriptionNumber: base.subscriptionNumber,
    interval: 'monthly',
    status: 'active',
    billingStatus: 'scheduled',
    serviceStatus: 'pending_setup',
    startDate,
    nextBillingDate: buildNextBillingDate(startDate, 'monthly'),
    lastBillingDate: '',
    endDate: '',
    linkedPaymentLinkKey: '',
    linkedOrderNumber: '',
    notes: '',
    internalNotes: '',
    paymentMode: 'recurring',
    autoCreatePaymentLink: true,
    sendPaymentLinkEmail: true,
    paymentLinkUpsellItems: [],
  };
};

const PaymentSubscriptionsPanel = ({
  clients,
  services,
  paymentLinks,
  displayCountry,
}: PaymentSubscriptionsPanelProps) => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [form, setForm] = useState<SubscriptionFormState>(() =>
    buildInitialState(currencyService.getDisplayCurrencyCode(displayCountry))
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastCreatedSubscription, setLastCreatedSubscription] =
    useState<AdminSubscription | null>(null);
  const [selectedSubscription, setSelectedSubscription] =
    useState<AdminSubscription | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'create'>('view');
  const [availableServices, setAvailableServices] = useState<Service[]>(services);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);

  const loadSubscriptions = async () => {
    setIsLoading(true);

    try {
      const data = await adminSubscriptionService.getSubscriptions(true);
      setSubscriptions(data);
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement des abonnements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (services.length > 0) {
      setAvailableServices(services);
    }
  }, [services]);

  useEffect(() => {
    if (services.length > 0) {
      return;
    }

    const loadServicesCatalog = async () => {
      try {
        const catalog = await getAllServices(true);
        if (catalog.length > 0) {
          setAvailableServices(catalog);
          return;
        }

        const response = await fetch('/data/services.json', {
          credentials: 'include',
          cache: 'no-store',
        });
        const payload = (await response.json().catch(() => ({}))) as {
          services?: Service[];
        };
        setAvailableServices(Array.isArray(payload.services) ? payload.services : []);
      } catch (error) {
        console.error(error);
      }
    };

    loadServicesCatalog().catch((error) => {
      console.error(error);
    });
  }, [services.length]);

  useEffect(() => {
    setForm((current) => {
      if (
        current.selectedClientId ||
        current.selectedServiceId ||
        current.amount ||
        current.currencyCode === displayCurrencyCode
      ) {
        return current;
      }

      return {
        ...current,
        currencyCode: displayCurrencyCode,
      };
    });
  }, [displayCurrencyCode]);

  const resetForm = () => {
    setForm(buildInitialState(displayCurrencyCode));
  };

  const formatOriginalAmount = (amount: number, currencyCode: string) =>
    currencyService.formatCurrencyAmount(amount, currencyCode, {
      display: 'code',
    });

  const formatDisplayAmount = (amount: number, currencyCode: string) =>
    currencyService.formatAmountForDisplay(amount, currencyCode, {
      country: displayCountry,
      display: 'code',
    });

  const buildPaymentLinkLineItemsFromForm = (
    state: SubscriptionFormState,
    strict = false
  ): {
    items: Array<{
      id?: string;
      label: string;
      quantity: number;
      unitAmount: number;
      lineTotal: number;
    }>;
    totalAmount: number;
    serviceName: string;
  } => {
    const items: Array<{
      id?: string;
      label: string;
      quantity: number;
      unitAmount: number;
      lineTotal: number;
    }> = [];

    const baseLabel = state.serviceName.trim();
    const baseAmountRaw = Number(state.amount || 0);
    const baseAmount =
      Number.isFinite(baseAmountRaw) && baseAmountRaw > 0
        ? Number(baseAmountRaw.toFixed(2))
        : 0;

    if (baseLabel || state.amount.trim()) {
      if (strict && !baseLabel) {
        throw new Error('Le produit principal de l’abonnement doit avoir un libellé.');
      }

      if (strict && baseAmount <= 0) {
        throw new Error(
          'Le prix du produit principal abonnement doit être supérieur à 0.'
        );
      }

      if (baseLabel && baseAmount > 0) {
        items.push({
          id: 'item_main',
          label: baseLabel,
          quantity: 1,
          unitAmount: baseAmount,
          lineTotal: baseAmount,
        });
      }
    }

    state.paymentLinkUpsellItems.forEach((upsell, index) => {
      const label = upsell.label.trim();
      const quantityRaw = Number(upsell.quantity || 1);
      const quantity =
        Number.isFinite(quantityRaw) && Math.trunc(quantityRaw) > 0
          ? Math.trunc(quantityRaw)
          : 1;
      const unitAmountRaw = Number(upsell.unitAmount || 0);
      const unitAmount =
        Number.isFinite(unitAmountRaw) && unitAmountRaw > 0
          ? Number(unitAmountRaw.toFixed(2))
          : 0;
      const hasAnyValue = Boolean(label || upsell.unitAmount.trim() || upsell.quantity.trim());

      if (!hasAnyValue) {
        return;
      }

      if (strict && !label) {
        throw new Error(`Upsell abonnement ${index + 1}: libellé obligatoire.`);
      }

      if (strict && unitAmount <= 0) {
        throw new Error(
          `Upsell abonnement ${index + 1}: le prix unitaire doit être supérieur à 0.`
        );
      }

      if (label && unitAmount > 0) {
        items.push({
          id: upsell.id,
          label,
          quantity,
          unitAmount,
          lineTotal: Number((unitAmount * quantity).toFixed(2)),
        });
      }
    });

    const totalAmount = Number(
      items.reduce((sum, entry) => sum + entry.lineTotal, 0).toFixed(2)
    );
    const serviceName = items[0]?.label || baseLabel;

    return {
      items,
      totalAmount,
      serviceName,
    };
  };

  const computedPaymentLinkDraft = buildPaymentLinkLineItemsFromForm(form, false);

  const handleAddPaymentUpsell = () => {
    setForm((current) => ({
      ...current,
      paymentLinkUpsellItems: [
        ...current.paymentLinkUpsellItems,
        createSubscriptionPaymentLineItem(),
      ],
    }));
  };

  const handleRemovePaymentUpsell = (itemId: string) => {
    setForm((current) => ({
      ...current,
      paymentLinkUpsellItems: current.paymentLinkUpsellItems.filter(
        (entry) => entry.id !== itemId
      ),
    }));
  };

  const handlePaymentUpsellChange = (
    itemId: string,
    field: keyof Omit<SubscriptionPaymentLineItemForm, 'id'>,
    value: string
  ) => {
    setForm((current) => ({
      ...current,
      paymentLinkUpsellItems: current.paymentLinkUpsellItems.map((entry) =>
        entry.id === itemId
          ? {
              ...entry,
              [field]: value,
            }
          : entry
      ),
    }));
  };

  const paymentLinksByKey = useMemo(() => {
    const map = new Map<string, AdminPaymentLink>();
    paymentLinks.forEach((paymentLink) => {
      map.set(paymentLink.paymentLinkKey, paymentLink);
    });
    return map;
  }, [paymentLinks]);

  const handleClientChange = (clientId: string) => {
    const client = clients.find((entry) => entry.id === clientId);
    if (!client) {
      setForm((current) => ({
        ...current,
        selectedClientId: '',
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      selectedClientId: client.id,
      clientName: client.name,
      clientEmail: client.email,
      clientPhone: client.phone || '',
      clientCompany: client.company || '',
      selectedServiceId: current.selectedServiceId || client.serviceId || '',
      serviceName: current.serviceName || client.serviceName || '',
      amount:
        current.amount ||
        (client.totalAmount > 0 ? String(client.totalAmount.toFixed(2)) : ''),
      currencyCode: client.currencyCode || current.currencyCode || 'EUR',
    }));
  };

  const handleServiceChange = (serviceId: string) => {
    const service = availableServices.find((entry) => entry.id === serviceId);
    if (!service) {
      setForm((current) => ({
        ...current,
        selectedServiceId: '',
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      selectedServiceId: service.id,
      serviceName: service.name,
      amount:
        service.price > 0
          ? currencyService
              .convertAmount(
                service.price,
                'EUR',
                current.currencyCode || displayCurrencyCode
              )
              .toFixed(2)
          : current.amount,
    }));
  };

  const handleLinkedPaymentChange = (paymentLinkKey: string) => {
    const linkedPayment = paymentLinks.find(
      (entry) => entry.paymentLinkKey === paymentLinkKey
    );
    const linkedMainItem = linkedPayment?.lineItems[0];
    const linkedUpsells =
      linkedPayment && linkedPayment.lineItems.length > 1
        ? linkedPayment.lineItems.slice(1).map((item) => ({
            id: item.id || createSubscriptionPaymentLineItem().id,
            label: item.label,
            quantity: String(Math.max(1, Math.trunc(Number(item.quantity || 1)))),
            unitAmount: Number(item.unitAmount || 0).toFixed(2),
          }))
        : [];

    setForm((current) => ({
      ...current,
      linkedPaymentLinkKey: paymentLinkKey,
      clientName: linkedPayment?.clientName || current.clientName,
      clientEmail: linkedPayment?.clientEmail || current.clientEmail,
      clientPhone: linkedPayment?.clientPhone || current.clientPhone,
      clientCompany: linkedPayment?.clientCompany || current.clientCompany,
      serviceName: linkedMainItem?.label || linkedPayment?.serviceName || current.serviceName,
      amount:
        linkedMainItem && Number(linkedMainItem.lineTotal || 0) > 0
          ? String(Number(linkedMainItem.lineTotal || 0).toFixed(2))
          : linkedPayment && Number(linkedPayment.amount || 0) > 0
            ? String(linkedPayment.amount.toFixed(2))
          : current.amount,
      currencyCode: linkedPayment?.currencyCode || current.currencyCode,
      notes:
        linkedPayment?.quoteNumber && !current.notes
          ? `Abonnement lié au devis ${linkedPayment.quoteNumber}.`
          : current.notes,
      paymentMode: linkedPayment?.paymentMode || current.paymentMode,
      paymentLinkUpsellItems: linkedUpsells,
    }));
  };

  const handleCreateSubscription = async () => {
    setIsSubmitting(true);

    try {
      let resolvedPaymentLinkKey = form.linkedPaymentLinkKey;
      let createdPaymentLink: AdminPaymentLink | null = null;

      if (!resolvedPaymentLinkKey && form.autoCreatePaymentLink) {
        if (!form.clientName.trim() || !form.clientEmail.trim() || !form.serviceName.trim()) {
          throw new Error(
            'Renseigne client, email et libellé service pour générer automatiquement le lien de paiement.'
          );
        }

        const computed = buildPaymentLinkLineItemsFromForm(form, true);
        if (computed.items.length === 0) {
          throw new Error('Ajoute au moins un produit pour générer le lien de paiement.');
        }

        const paymentAmount = computed.totalAmount;
        if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
          throw new Error(
            'Montant invalide pour générer le lien de paiement abonnement. Indique un montant supérieur à 0.'
          );
        }

        const lineItems = computed.items.map((entry) => ({
          id: entry.id,
          label: entry.label,
          quantity: entry.quantity,
          unitAmount: entry.unitAmount,
        }));

        const paymentResult = await adminPaymentLinkService.createPaymentLink({
          clientId: form.selectedClientId || undefined,
          clientName: form.clientName,
          clientEmail: form.clientEmail,
          clientPhone: form.clientPhone || undefined,
          clientCompany: form.clientCompany || undefined,
          serviceId: form.selectedServiceId || undefined,
          serviceName: computed.serviceName,
          paymentMode: form.paymentMode,
          linkedSubscriptionId: '',
          linkedSubscriptionNumber: form.subscriptionNumber,
          recurringInterval: form.interval,
          recurringStartDate: form.nextBillingDate || undefined,
          recurringAmount: Number(form.amount || 0),
          amount: paymentAmount,
          lineItems,
          currencyCode: form.currencyCode,
          notes: form.internalNotes || undefined,
          clientMessage: form.notes || undefined,
          sendEmail: form.sendPaymentLinkEmail,
          emailTemplateAlias: 'checkout-quote',
        });

        createdPaymentLink = paymentResult.paymentLink;
        resolvedPaymentLinkKey = paymentResult.paymentLink.paymentLinkKey;

        if (paymentResult.emailSent) {
          toast.success('Lien de paiement abonnement créé et email envoyé.');
        } else if (paymentResult.emailError) {
          toast.success('Lien de paiement abonnement créé.');
          toast.error(`Email non envoyé: ${paymentResult.emailError}`);
        } else {
          toast.success('Lien de paiement abonnement créé.');
        }
      }

      const linkedPayment =
        createdPaymentLink ||
        (resolvedPaymentLinkKey
          ? paymentLinksByKey.get(resolvedPaymentLinkKey) || null
          : null);

      const subscription = await adminSubscriptionService.saveSubscription({
        ...createEmptySubscription(),
        subscriptionNumber: form.subscriptionNumber,
        clientId: form.selectedClientId,
        clientName: form.clientName,
        clientEmail: form.clientEmail,
        clientPhone: form.clientPhone,
        clientCompany: form.clientCompany,
        serviceId: form.selectedServiceId,
        serviceName: form.serviceName,
        paymentMode: form.paymentMode,
        amount: Number(form.amount || 0),
        currencyCode: form.currencyCode,
        interval: form.interval,
        status: form.status,
        billingStatus: form.billingStatus,
        serviceStatus: form.serviceStatus,
        startDate: form.startDate
          ? new Date(`${form.startDate}T12:00:00`).toISOString()
          : new Date().toISOString(),
        nextBillingDate: form.nextBillingDate
          ? new Date(`${form.nextBillingDate}T12:00:00`).toISOString()
          : '',
        lastBillingDate: form.lastBillingDate
          ? new Date(`${form.lastBillingDate}T12:00:00`).toISOString()
          : '',
        endDate: form.endDate
          ? new Date(`${form.endDate}T12:00:00`).toISOString()
          : '',
        linkedOrderNumber: form.linkedOrderNumber,
        paymentLinkKey: resolvedPaymentLinkKey,
        mollieCustomerId: linkedPayment?.mollieCustomerId || '',
        mollieSubscriptionId: linkedPayment?.mollieSubscriptionId || '',
        recurringInterval:
          linkedPayment?.recurringInterval || (form.paymentMode === 'recurring' ? form.interval : ''),
        recurringStatus:
          linkedPayment?.recurringStatus || (form.paymentMode === 'recurring' ? 'pending' : ''),
        recurringActivatedAt: linkedPayment?.recurringActivatedAt || '',
        notes: form.notes,
        internalNotes: form.internalNotes,
      });

      setLastCreatedSubscription(subscription);
      resetForm();
      await loadSubscriptions();
      toast.success(
        createdPaymentLink
          ? 'Abonnement créé avec lien de paiement automatique.'
          : 'Abonnement créé et enregistré dans le suivi admin.'
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Impossible de créer l'abonnement"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async (subscription: AdminSubscription) => {
    try {
      await adminSubscriptionService.saveSubscription(subscription);
      await loadSubscriptions();
      setSelectedSubscription(null);
      toast.success(
        modalMode === 'create'
          ? 'Abonnement créé avec succès !'
          : 'Abonnement mis à jour !'
      );
    } catch (error) {
      console.error(error);
      toast.error("Impossible de sauvegarder cet abonnement");
    }
  };

  const handleDelete = async (subscription: AdminSubscription) => {
    if (
      !window.confirm(
        `Supprimer l'abonnement ${subscription.subscriptionNumber} ?`
      )
    ) {
      return;
    }

    try {
      await adminSubscriptionService.removeSubscription(subscription.id);
      await loadSubscriptions();
      setSelectedSubscription(null);
      toast.success('Abonnement supprimé !');
    } catch (error) {
      console.error(error);
      toast.error("Impossible de supprimer cet abonnement");
    }
  };

  const metrics = useMemo(() => {
    return subscriptions.reduce(
      (accumulator, subscription) => {
        accumulator.total += 1;
        accumulator.mrr += currencyService.convertToDisplayAmount(
          subscription.amount,
          subscription.currencyCode,
          displayCountry
        );

        if (subscription.status === 'active') {
          accumulator.active += 1;
        }

        if (subscription.status === 'past_due') {
          accumulator.overdue += 1;
        }

        const nextBilling = subscription.nextBillingDate
          ? new Date(subscription.nextBillingDate)
          : null;
        const daysUntilBilling =
          nextBilling && !Number.isNaN(nextBilling.getTime())
            ? Math.ceil(
                (nextBilling.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
              )
            : null;

        if (daysUntilBilling !== null && daysUntilBilling >= 0 && daysUntilBilling <= 14) {
          accumulator.upcoming += 1;
        }

        return accumulator;
      },
      {
        total: 0,
        active: 0,
        overdue: 0,
        upcoming: 0,
        mrr: 0,
      }
    );
  }, [subscriptions, displayCountry]);

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Abonnements</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{metrics.total}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Actifs</div>
          <div className="text-3xl font-bold text-emerald-600 mt-2">{metrics.active}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">En retard</div>
          <div className="text-3xl font-bold text-red-600 mt-2">{metrics.overdue}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Echéances proches</div>
          <div className="text-3xl font-bold text-amber-600 mt-2">{metrics.upcoming}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">MRR suivi ({displayCurrencyCode})</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">
            {currencyService.formatCurrencyAmount(metrics.mrr, displayCurrencyCode, {
              country: displayCountry,
              display: 'code',
            })}
          </div>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[420px_minmax(0,1fr)]">
        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Nouvel abonnement</h3>
            <p className="text-sm text-gray-500 mt-1">
              Prépare un abonnement avec fréquence, dates de facturation et statut
              de delivery directement depuis la zone paiements.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lier un devis / lien de paiement existant
            </label>
            <select
              value={form.linkedPaymentLinkKey}
              onChange={(event) => handleLinkedPaymentChange(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              <option value="">Aucun lien relié</option>
              {paymentLinks.map((paymentLink) => (
                <option key={paymentLink.paymentLinkKey} value={paymentLink.paymentLinkKey}>
                  {paymentLink.quoteNumber} • {paymentLink.clientName}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl bg-gray-50 px-4 py-3 border border-gray-200 space-y-2">
              <div className="text-sm text-gray-700 font-medium">Type de paiement à générer</div>
              <div className="grid gap-2">
                {PAYMENT_MODE_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="subscriptionPaymentMode"
                      value={option.value}
                      checked={form.paymentMode === option.value}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          paymentMode: event.target.value as SubscriptionPaymentMode,
                        }))
                      }
                      disabled={!form.autoCreatePaymentLink}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Récurrent: premier paiement pour activer le mandat, puis prélèvements automatiques.
              </p>
            </div>

            <label className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 border border-gray-200">
              <input
                type="checkbox"
                checked={form.autoCreatePaymentLink}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    autoCreatePaymentLink: event.target.checked,
                  }))
                }
              />
              <span className="text-sm text-gray-700">
                Créer automatiquement un lien de paiement pour cet abonnement
              </span>
            </label>

            <label className="flex items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 border border-gray-200">
              <input
                type="checkbox"
                checked={form.sendPaymentLinkEmail}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    sendPaymentLinkEmail: event.target.checked,
                  }))
                }
                disabled={!form.autoCreatePaymentLink}
              />
              <span className="text-sm text-gray-700">
                Envoyer l&apos;email du lien de paiement dès la création
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client existant
            </label>
            <select
              value={form.selectedClientId}
              onChange={(event) => handleClientChange(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              <option value="">Sélection libre</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} • {client.email}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom client</label>
              <input
                type="text"
                value={form.clientName}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientName: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.clientEmail}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientEmail: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input
                type="text"
                value={form.clientPhone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientPhone: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
              <input
                type="text"
                value={form.clientCompany}
                onChange={(event) =>
                  setForm((current) => ({ ...current, clientCompany: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service catalogué
            </label>
            <select
              value={form.selectedServiceId}
              onChange={(event) => handleServiceChange(event.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              <option value="">Personnalisé</option>
              {availableServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
            {availableServices.length === 0 && (
              <p className="mt-2 text-xs text-amber-700">
                Catalogue services en chargement. Tu peux écrire le libellé manuellement
                juste en dessous.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Libellé abonnement
            </label>
            <input
              type="text"
              value={form.serviceName}
              onChange={(event) =>
                setForm((current) => ({ ...current, serviceName: event.target.value }))
              }
              placeholder="Ex: Forfait media buying 30 jours"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant abonnement (base récurrente)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(event) =>
                  setForm((current) => ({ ...current, amount: event.target.value }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
              {Number(form.amount || 0) > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  Affichage admin actuel:{' '}
                  {formatDisplayAmount(Number(form.amount || 0), form.currencyCode)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
              <select
                value={form.currencyCode}
                onChange={(event) =>
                  setForm((current) => {
                    const nextCurrencyCode = event.target.value;
                    const selectedService = availableServices.find(
                      (entry) => entry.id === current.selectedServiceId
                    );

                    return {
                      ...current,
                      currencyCode: nextCurrencyCode,
                      amount:
                        selectedService && selectedService.price > 0
                          ? currencyService
                              .convertAmount(selectedService.price, 'EUR', nextCurrencyCode)
                              .toFixed(2)
                          : current.amount,
                    };
                  })
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                <option value="EUR">EUR</option>
                <option value="CAD">CAD</option>
                <option value="CHF">CHF</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium text-gray-700">
                Upsells lien de paiement initial
              </label>
              <button
                type="button"
                onClick={handleAddPaymentUpsell}
                className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
              >
                + Ajouter produit
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Optionnel: ces produits s&apos;ajoutent au premier paiement, sans changer la base MRR de l&apos;abonnement.
            </p>

            {form.paymentLinkUpsellItems.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                Aucun upsell initial pour le moment.
              </div>
            ) : (
              <div className="space-y-3">
                {form.paymentLinkUpsellItems.map((upsell, index) => {
                  const quantity = Math.max(1, Math.trunc(Number(upsell.quantity || 1)));
                  const unitAmount = Number(upsell.unitAmount || 0);
                  const lineTotal =
                    Number.isFinite(unitAmount) && unitAmount > 0
                      ? Number((unitAmount * quantity).toFixed(2))
                      : 0;

                  return (
                    <div
                      key={upsell.id}
                      className="rounded-xl border border-gray-200 p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                          Upsell {index + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemovePaymentUpsell(upsell.id)}
                          className="px-2 py-1 rounded-md text-xs text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>

                      <div className="grid gap-3 md:grid-cols-6">
                        <div className="md:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Produit
                          </label>
                          <input
                            type="text"
                            value={upsell.label}
                            onChange={(event) =>
                              handlePaymentUpsellChange(upsell.id, 'label', event.target.value)
                            }
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>

                        <div className="md:col-span-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Qté
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={upsell.quantity}
                            onChange={(event) =>
                              handlePaymentUpsellChange(upsell.id, 'quantity', event.target.value)
                            }
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Prix unitaire
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={upsell.unitAmount}
                            onChange={(event) =>
                              handlePaymentUpsellChange(upsell.id, 'unitAmount', event.target.value)
                            }
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
                          />
                        </div>
                      </div>

                      <div className="text-xs text-gray-500">
                        Sous-total upsell:{' '}
                        {lineTotal > 0
                          ? formatDisplayAmount(lineTotal, form.currencyCode)
                          : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#f6d8c0] bg-[#fffaf5] p-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-gray-800">
                Total du lien de paiement initial
              </span>
              <span className="text-base font-bold text-neon-orange">
                {formatDisplayAmount(computedPaymentLinkDraft.totalAmount, form.currencyCode)}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {computedPaymentLinkDraft.items.length} produit(s) sur le lien auto-généré.
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro abonnement
              </label>
              <input
                type="text"
                value={form.subscriptionNumber}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    subscriptionNumber: event.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fréquence
              </label>
              <select
                value={form.interval}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    interval: event.target.value as SubscriptionInterval,
                    nextBillingDate: buildNextBillingDate(
                      current.startDate,
                      event.target.value as SubscriptionInterval
                    ),
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                {INTERVAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    status: event.target.value as SubscriptionStatus,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facturation
              </label>
              <select
                value={form.billingStatus}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    billingStatus: event.target.value as SubscriptionBillingStatus,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                {BILLING_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery service
              </label>
              <select
                value={form.serviceStatus}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    serviceStatus: event.target.value as SubscriptionServiceStatus,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
              >
                {SERVICE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Début abonnement
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    startDate: event.target.value,
                    nextBillingDate: buildNextBillingDate(
                      event.target.value,
                      current.interval
                    ),
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prochaine échéance
              </label>
              <input
                type="date"
                value={form.nextBillingDate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    nextBillingDate: event.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dernier paiement
              </label>
              <input
                type="date"
                value={form.lastBillingDate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    lastBillingDate: event.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fin prévue
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    endDate: event.target.value,
                  }))
                }
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commande liée
              </label>
              <input
                type="text"
                value={form.linkedOrderNumber}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    linkedOrderNumber: event.target.value,
                  }))
                }
                placeholder="Ex: CMD-2026-0001"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes visibles client
            </label>
            <textarea
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
              rows={3}
              placeholder="Ex: Abonnement actif après validation de la mise en route."
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes internes
            </label>
            <textarea
              value={form.internalNotes}
              onChange={(event) =>
                setForm((current) => ({ ...current, internalNotes: event.target.value }))
              }
              rows={3}
              placeholder="Visible seulement dans l’admin."
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCreateSubscription}
              disabled={isSubmitting}
              className="flex-1 px-5 py-3 rounded-xl bg-neon-orange text-white hover:bg-[#FF4500] transition-colors disabled:opacity-60"
            >
              {isSubmitting ? 'Création...' : "Créer l'abonnement"}
            </button>
            <button
              onClick={resetForm}
              className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </section>

        <section className="space-y-6">
          {lastCreatedSubscription && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-blue-700">
                    Dernier abonnement créé
                  </div>
                  <div className="text-lg font-bold text-gray-900 mt-1">
                    {lastCreatedSubscription.subscriptionNumber} •{' '}
                    {lastCreatedSubscription.serviceName}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {lastCreatedSubscription.clientName} •{' '}
                    {formatDisplayAmount(
                      lastCreatedSubscription.amount,
                      lastCreatedSubscription.currencyCode
                    )}{' '}
                    • {formatDate(lastCreatedSubscription.nextBillingDate)}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Paiement: {lastCreatedSubscription.paymentMode === 'recurring' ? 'Récurrent' : 'Unique'}
                    {lastCreatedSubscription.recurringStatus
                      ? ` • statut: ${lastCreatedSubscription.recurringStatus}`
                      : ''}
                  </div>
                  {lastCreatedSubscription.paymentLinkKey && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href={`/devis/${encodeURIComponent(lastCreatedSubscription.paymentLinkKey)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                      >
                        Ouvrir devis
                      </a>
                      {paymentLinksByKey.get(lastCreatedSubscription.paymentLinkKey)
                        ?.paymentUrl && (
                        <a
                          href={
                            paymentLinksByKey.get(lastCreatedSubscription.paymentLinkKey)
                              ?.paymentUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          Ouvrir paiement
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setSelectedSubscription(lastCreatedSubscription);
                    setModalMode('view');
                  }}
                  className="px-4 py-2 rounded-lg bg-white text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  Ouvrir la fiche
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                Historique des abonnements
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Suis les statuts, les dates d&apos;échéance et l&apos;avancement
                delivery des forfaits récurrents.
              </p>
            </div>

            {isLoading ? (
              <div className="py-16 text-center text-gray-600">
                Chargement des abonnements...
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="py-16 text-center text-gray-600">
                Aucun abonnement créé pour le moment.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {subscriptions.slice(0, 8).map((subscription, index) => {
                  const linkedPayment = subscription.paymentLinkKey
                    ? paymentLinksByKey.get(subscription.paymentLinkKey)
                    : undefined;
                  const quoteUrl = subscription.paymentLinkKey
                    ? linkedPayment?.quoteUrl ||
                      `/devis/${encodeURIComponent(subscription.paymentLinkKey)}`
                    : '';
                  const paymentUrl = linkedPayment?.paymentUrl || '';

                  return (
                  <motion.div
                    key={subscription.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-6"
                  >
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
                      <div className="space-y-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {subscription.subscriptionNumber}
                          </h4>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGES[subscription.status]}`}
                          >
                            {STATUS_LABELS[subscription.status]}
                          </span>
                          <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-600">
                            {
                              INTERVAL_OPTIONS.find(
                                (option) => option.value === subscription.interval
                              )?.label
                            }
                          </span>
                        </div>

                        <div className="text-gray-900 font-medium">
                          {subscription.serviceName}
                        </div>
                        <div className="text-xs text-gray-500">
                          Paiement: {subscription.paymentMode === 'recurring' ? 'Récurrent' : 'Unique'}
                          {subscription.recurringStatus ? ` • ${subscription.recurringStatus}` : ''}
                        </div>
                        <div className="text-sm text-gray-600">
                          {subscription.clientName} • {subscription.clientEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDisplayAmount(
                            subscription.amount,
                            subscription.currencyCode
                          )}{' '}
                          • prochaine échéance {formatDate(subscription.nextBillingDate)}
                          {subscription.currencyCode !== displayCurrencyCode && (
                            <span className="block text-xs text-gray-400 mt-1">
                              Origine:{' '}
                              {formatOriginalAmount(
                                subscription.amount,
                                subscription.currencyCode
                              )}
                            </span>
                          )}
                        </div>

                        <div className="grid gap-2 text-xs text-gray-500 md:grid-cols-2">
                          <div>Début: {formatDate(subscription.startDate)}</div>
                          <div>Dernier paiement: {formatDate(subscription.lastBillingDate)}</div>
                          <div>Fin: {formatDate(subscription.endDate)}</div>
                          <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                            <span>Lien paiement:</span>
                            {subscription.paymentLinkKey ? (
                              <>
                                <a
                                  href={quoteUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  Ouvrir devis
                                </a>
                                {paymentUrl ? (
                                  <a
                                    href={paymentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    Ouvrir paiement
                                  </a>
                                ) : (
                                  <span className="text-gray-400">
                                    paiement direct indisponible
                                  </span>
                                )}
                                <a
                                  href={getInvoicePdfUrl(subscription.paymentLinkKey)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  Facture PDF
                                </a>
                                <span className="text-gray-400">{subscription.paymentLinkKey}</span>
                              </>
                            ) : (
                              <span>non relié</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 xl:justify-end">
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setModalMode('view');
                          }}
                          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Ouvrir
                        </button>
                        <button
                          onClick={() => handleDelete(subscription)}
                          className="px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {selectedSubscription && (
        <SubscriptionDetailsModal
          subscription={selectedSubscription}
          clients={clients}
          services={availableServices}
          defaultMode={modalMode}
          onClose={() => setSelectedSubscription(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default PaymentSubscriptionsPanel;
